import {
  instantFromEpochMilliseconds,
  nowEpochMilliseconds,
  toHttpDate,
} from "@nijiurachan/js/util/time";

export const FORCE_PC_COOKIE_NAME = "force_pc";
export const FORCE_MOBILE_COOKIE_NAME = "force_mobile";
/** 強制指定Cookie(PC/SP共通)の有効期間。 */
export const FORCE_PC_MAX_AGE_SECONDS: number = 24 * 60 * 60;
/** PC版レイアウトを自動選択する最小ビューポート幅(px)。この値未満はSP版。 */
export const DESKTOP_MIN_WIDTH_PX = 768;

/**
 * 表示モードの明示指定。
 * - `"auto"`: 明示指定なし。ウィンドウのpointer特性/幅から自動判定する
 * - `"pc"`: 常にPC版を表示する
 * - `"mobile"`: 常にSP版を表示する
 */
export type ForceDisplayMode = "auto" | "pc" | "mobile";

export type ForcePcCookieAction = "set-pc" | "set-mobile" | "clear" | "none";

export interface ForcePcPreference {
  mode: ForceDisplayMode;
  cookieAction: ForcePcCookieAction;
}

export interface CurrentLocationParts {
  pathname: string;
  search: string;
  hash: string;
}

export type DesktopDisplayMode =
  | "mobile"
  | "responsive-desktop"
  | "forced-desktop"
  | "forced-mobile";

/**
 * ビューポート幅から自動判定時のPC/SP表示を決める。
 * `viewportIsDesktop` は `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)` の照合結果で、
 * `true` のとき PC版レイアウトを選ぶ。pointer特性は考慮しない(幅のみで切り替え、
 * Tailwind の `md:` prefix (768px) と境界を揃えるため)。
 */
export function resolveAutoDesktopMode(viewportIsDesktop: boolean): boolean {
  return viewportIsDesktop;
}

export function buildForcePcUrl(location: CurrentLocationParts): string {
  return buildVersionUrl(location, "1");
}

export function buildMobileVersionUrl(location: CurrentLocationParts): string {
  return buildVersionUrl(location, "0");
}

/**
 * 現在URLから`pc`クエリだけを取り除いたURL文字列を返す（pathname/hash/他クエリは保持）。
 * `pc=1`/`pc=0`をCookieへ同期反映した直後にhistory.replaceStateで適用し、URLクエリを
 * ワンショット化するために使う。以後のリロード（ブックマーク/設定画面のreload等）で
 * `pc`が再解決されず、Cookie側の設定（force_mobile含む）を無断で上書きし続けない。
 */
export function stripPcQuery(location: CurrentLocationParts): string {
  const queryParts = stripPrefix(location.search, "?")
    .split("&")
    .filter((part) => part !== "" && getQueryKey(part) !== "pc");
  const query = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";

  return `${location.pathname}${query}${location.hash}`;
}

/**
 * URLクエリ`pc=1`/`pc=0`またはCookieから表示モードの明示指定を解決する。
 * URLクエリが指定されていれば常にCookieより優先し、Cookieへの同期(`cookieAction`)を要求する。
 * `pc=1`は「常にPC版」を、`pc=0`は明示指定の解除(自動判定へ戻す)を表す。
 * 「常にSP版」への固定はURLクエリ経由では行わず、設定画面から
 * `createForceDisplayModeCookies("set-mobile")`で直接Cookieを書き込む
 * (SideMenu/DesktopHeaderの既存リンクの意味を変えないため)。
 */
export function resolveForcePcPreference(
  search: string,
  cookieHeader: string,
): ForcePcPreference {
  const pcValues = new URLSearchParams(stripPrefix(search, "?")).getAll("pc");

  if (pcValues.includes("0")) {
    return { mode: "auto", cookieAction: "clear" };
  }
  if (pcValues.includes("1")) {
    return { mode: "pc", cookieAction: "set-pc" };
  }

  return {
    mode: readForceDisplayModeFromCookies(cookieHeader),
    cookieAction: "none",
  };
}

/** 強制指定(forceMode)は自動判定(viewportIsDesktop)より常に優先される。 */
export function resolveDesktopDisplayMode(
  viewportIsDesktop: boolean,
  forceMode: ForceDisplayMode,
): DesktopDisplayMode {
  if (forceMode === "pc") return "forced-desktop";
  if (forceMode === "mobile") return "forced-mobile";
  return viewportIsDesktop ? "responsive-desktop" : "mobile";
}

/**
 * 指定した表示モードのアクションを反映するために書き込むCookie文字列の一覧を返す。
 * `document.cookie`への代入は1回につき1つのCookieしか設定できないため、
 * PC⇔SP切り替え時は「新しいモードのCookieを立てる」と「反対側の古いCookieを消す」の
 * 2本を返すことがある。呼び出し側は返された要素を順に`document.cookie = ...`する。
 *
 * `force_pc`Cookie(値は常に`"1"`または未設定)は、旧来のPHP版(AI_BBS)の
 * `force_pc`Cookie読み取りと値の意味を完全互換に保つため、既存の互換の
 * ためにこのCookieの値を変更しない。「常にSP版」は別名の`force_mobile`
 * Cookieで表現し、`force_pc`の値空間・isset判定に一切影響しないようにする。
 */
export function createForceDisplayModeCookies(
  action: Exclude<ForcePcCookieAction, "none">,
  now: number = nowEpochMilliseconds(),
): string[] {
  if (action === "clear") {
    return [
      deleteCookie(FORCE_PC_COOKIE_NAME),
      deleteCookie(FORCE_MOBILE_COOKIE_NAME),
    ];
  }
  if (action === "set-pc") {
    return [
      setCookie(FORCE_PC_COOKIE_NAME, now),
      deleteCookie(FORCE_MOBILE_COOKIE_NAME),
    ];
  }
  return [
    setCookie(FORCE_MOBILE_COOKIE_NAME, now),
    deleteCookie(FORCE_PC_COOKIE_NAME),
  ];
}

function setCookie(name: string, now: number): string {
  const expiresAt = instantFromEpochMilliseconds(now).add({
    seconds: FORCE_PC_MAX_AGE_SECONDS,
  });
  const expires = toHttpDate(expiresAt.epochMilliseconds);
  return `${name}=1; Expires=${expires}; Max-Age=${FORCE_PC_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
}

function deleteCookie(name: string): string {
  return `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; Path=/; SameSite=Lax`;
}

function buildVersionUrl(
  location: CurrentLocationParts,
  pcValue: "0" | "1",
): string {
  const queryParts = stripPrefix(location.search, "?")
    .split("&")
    .filter((part) => part !== "" && getQueryKey(part) !== "pc");
  queryParts.push(`pc=${pcValue}`);

  return `${location.pathname}?${queryParts.join("&")}${location.hash}`;
}

function getQueryKey(queryPart: string): string {
  const rawKey = queryPart.split("=", 1)[0] ?? "";
  try {
    return decodeURIComponent(rawKey.replaceAll("+", " "));
  } catch {
    return rawKey;
  }
}

function readForceDisplayModeFromCookies(
  cookieHeader: string,
): ForceDisplayMode {
  if (getCookieValue(cookieHeader, FORCE_PC_COOKIE_NAME) === "1") return "pc";
  if (getCookieValue(cookieHeader, FORCE_MOBILE_COOKIE_NAME) === "1")
    return "mobile";
  return "auto";
}

function getCookieValue(
  cookieHeader: string,
  name: string,
): string | undefined {
  for (const part of cookieHeader.split(";")) {
    const separator = part.indexOf("=");
    if (separator < 0) continue;

    const cookieName = part.slice(0, separator).trim();
    if (cookieName !== name) continue;
    return part.slice(separator + 1).trim();
  }
  return undefined;
}

function stripPrefix(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}
